/**
 * Build-time feature flags for the member app.
 *
 * Same convention as REACT_APP_SHOW_DRAFT_BIOMARKER_COPY (see DataUploadsPage):
 * a `REACT_APP_*` string compared against 'true', read once at module load.
 * CRA inlines these at build time, so flipping one needs a rebuild + redeploy.
 */

/**
 * Body Map — member-facing symptom tracker (/body-map).
 *
 * DISABLED BY DEFAULT. Members must not reach it: the Home tile and both
 * Profile entry points are hidden, and the routes redirect to /home so a typed
 * or bookmarked URL does not work either.
 *
 * Fail-closed on purpose — an unset or misspelt env var leaves the feature OFF,
 * so a config slip cannot silently expose it. Set
 * REACT_APP_ENABLE_BODY_MAP=true to bring it back.
 *
 * Note this is a UI/route gate only. It stops new issues and check-ins being
 * created (and so stops the red-flag path opening clinical escalations), but it
 * does not change firestore.rules: existing `body_issues` documents remain
 * readable and writable by their owner at the data layer, and the ops/doctors
 * views of already-captured issues are untouched.
 */
export const BODY_MAP_ENABLED = process.env.REACT_APP_ENABLE_BODY_MAP === 'true';

/**
 * What the site root ('/') serves.
 *
 *   'login'  → redirect straight to /login. The public marketing home page is
 *              still reachable at /practice, and the sibling marketing routes
 *              (/membership, /assessments, /faq) are unchanged.
 *   default  → the public marketing home page at '/', as before.
 *
 * Defaults to the marketing page ON PURPOSE. This is shared source built for
 * both projects, so an unset variable must not strip the public site (and its
 * Apply funnel) off the production root. Staging opts in via
 * REACT_APP_LANDING_PAGE=login in .env.staging.
 */
export const LANDING_PAGE: 'login' | 'marketing' =
  process.env.REACT_APP_LANDING_PAGE === 'login' ? 'login' : 'marketing';

/**
 * Ask Melio — the test-cohort feedback chat (/feedback).
 *
 * Enables the route and the MemberRail entry point. Set in .env.staging; MUST
 * stay unset in production.
 *
 * UI GATE ONLY, and unlike BODY_MAP_ENABLED this one is not the whole story:
 * every chat callable independently checks isFeedbackChatEnabled() in
 * functions/chat/config.js, which requires BOTH a non-production Firebase
 * project AND the server-side flag. The production block there is a compiled-in
 * constant rather than configuration, so the feature cannot be switched on in
 * melio-health even by setting an env var. This flag exists so the entry points
 * can be dark-launched, not to control access.
 */
export const FEEDBACK_CHAT_ENABLED = process.env.REACT_APP_FEEDBACK_CHAT_ENABLED === 'true';

/**
 * How much of the public site is published (W-13).
 *
 *   'hero'   -> one screen. '/' is the hero and its two calls to action;
 *               /membership, /assessments, /faq and /practice redirect to it.
 *               /waitlist, /privacy and /terms stay reachable, with the header
 *               nav and the full footer stripped to a wordmark and a
 *               Privacy/Terms line.
 *   'full'   -> the whole marketing site, as before.
 *
 * Defaults to 'full' ON PURPOSE, matching LANDING_PAGE's reasoning: this is
 * shared source built for both projects, and an unset variable must not
 * silently unpublish the site on staging, where the full pages are what gets
 * reviewed. Production opts in via REACT_APP_PUBLIC_SITE=hero.
 *
 * Nothing is deleted by this flag - the pages, their routes and their content
 * all still exist. Flipping it back republishes them with a rebuild.
 */
export const PUBLIC_SITE: 'full' | 'hero' =
  process.env.REACT_APP_PUBLIC_SITE === 'hero' ? 'hero' : 'full';

/*
 * REACT_APP_DASHBOARD_V2 / DASHBOARD_V2_ENABLED has been retired.
 *
 * The rebuilt dashboard is now simply what /home renders, and
 * MemberHomeLegacy has been deleted (it carried its own instruction to go once
 * the flag was on). The flag was fail-closed, and with no .env.production in
 * the repo — `.env.*` is gitignored — it could never be true in a production
 * build, so every paying member was landing on the legacy page while the
 * rebuilt one sat dark. Rollback is now a revert rather than a flag flip.
 */
